import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, User, Phone, Mail, MapPin, Package, Clock, Calendar,
  CheckCircle, XCircle, Truck, DollarSign, FileText, Image,
  RotateCcw, ArrowLeftRight, Warehouse, ClipboardCheck, Ban,
  CheckCircle2, ChevronRight, Loader2, AlertCircle, Send,
  PackageCheck, History, Camera, Link, MessageSquare, Printer, Pencil, Plus, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status configuration
const statusConfig = {
  pending: { label: 'Pending', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: Clock },
  accepted: { label: 'Accepted', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: Ban },
  pickup_assigned: { label: 'Pickup Assigned', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', icon: Truck },
  picked_up: { label: 'Picked Up', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: Package },
  in_transit: { label: 'In Transit', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: Truck },
  delivered: { label: 'Delivered', color: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/30', icon: PackageCheck },
  qc_pending: { label: 'QC Pending', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: ClipboardCheck },
  inwarded: { label: 'Inwarded', color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', icon: Warehouse },
  refund_pending: { label: 'Refund Pending', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: DollarSign },
  completed: { label: 'Completed', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: CheckCircle2 },
  rto_in_transit: { label: 'RTO In Transit', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: RotateCcw },
};

// Return type configuration
const returnTypeConfig = {
  return_refund: { label: 'Return & Refund', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: RotateCcw },
  refund_only: { label: 'Refund Only', color: 'text-green-400', bg: 'bg-green-500/10', icon: DollarSign },
  replace_same: { label: 'Replace (Same)', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: ArrowLeftRight },
  replace_different: { label: 'Replace (Different)', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: ArrowLeftRight },
};

const ReturnDetailPanel = ({ returnData, isOpen, onClose, onReturnUpdated }) => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('details');
  
  // Dialog states
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [pickupDialogOpen, setPickupDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [replacementDialogOpen, setReplacementDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editFee, setEditFee] = useState(0);
  const [editCharges, setEditCharges] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  
  // Form states
  const [rejectReason, setRejectReason] = useState('');
  const [acceptNotes, setAcceptNotes] = useState('');
  const [restockingFee, setRestockingFee] = useState(0);
  const [pickupType, setPickupType] = useState('courier');
  const [courierType, setCourierType] = useState('');
  const [driverId, setDriverId] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [pickupNotes, setPickupNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [replacementOrderNumber, setReplacementOrderNumber] = useState('');
  
  // Data for dropdowns
  const [drivers, setDrivers] = useState([]);
  const [courierIntegrations, setCourierIntegrations] = useState([]);
  
  useEffect(() => {
    if (isOpen) {
      fetchDrivers();
      fetchCourierIntegrations();
    }
  }, [isOpen]);
  
  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/drivers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data.drivers || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };
  
  const fetchCourierIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/couriers/integrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourierIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error('Error fetching courier integrations:', error);
    }
  };
  
  if (!returnData) return null;
  
  const status = statusConfig[returnData.status] || statusConfig.pending;
  const returnType = returnTypeConfig[returnData.return_type] || returnTypeConfig.return_refund;
  const StatusIcon = status.icon;
  const TypeIcon = returnType.icon;
  
  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Action handlers
  const handleAccept = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/${returnData.id}/accept`, {
        restocking_fee: restockingFee,
        notes: acceptNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Return accepted successfully');
      setAcceptDialogOpen(false);
      onReturnUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept return');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReopen = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/${returnData.id}/reopen`, { reason: 'Customer escalation' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Return reopened — now pending for review');
      onReturnUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen return');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintSlip = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/v1/returns/${returnData.id}/pickup-slip`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to generate pickup slip');
    }
  };

  const openEditDialog = () => {
    setEditItems((returnData.items || []).map(it => ({
      sku: it.sku, product_name: it.product_name,
      quantity_ordered: it.quantity_ordered,
      quantity_returning: it.quantity_returning,
      reason: it.reason || ''
    })));
    setEditFee(returnData.restocking_fee || 0);
    setEditCharges((returnData.charges || []).map(c => ({ label: c.label, amount: c.amount })));
    setEditNotes(returnData.customer_notes || '');
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/${returnData.id}/edit`, {
        items: editItems.map(it => ({ sku: it.sku, reason: it.reason, quantity_returning: Number(it.quantity_returning) || 1 })),
        customer_notes: editNotes,
        restocking_fee: Number(editFee) || 0,
        charges: editCharges.filter(c => c.label).map(c => ({ label: c.label, amount: Number(c.amount) || 0 }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Return updated');
      setEditDialogOpen(false);
      onReturnUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update return');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/${returnData.id}/reject`, {
        reason: rejectReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Return rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      onReturnUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject return');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAssignPickup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/${returnData.id}/assign-pickup`, {
        pickup_type: pickupType,
        courier_type: pickupType === 'courier' ? courierType : null,
        driver_id: pickupType === 'driver' ? driverId : null,
        scheduled_date: scheduledDate || null,
        notes: pickupNotes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pickup assigned successfully');
      setPickupDialogOpen(false);
      onReturnUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign pickup');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/${returnData.id}/update-status?status=${newStatus}&notes=${encodeURIComponent(statusNotes || '')}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated successfully');
      setStatusDialogOpen(false);
      setNewStatus('');
      setStatusNotes('');
      onReturnUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkRefunded = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/${returnData.id}/mark-refunded?notes=${encodeURIComponent(refundNotes || '')}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Return marked as refunded');
      setRefundDialogOpen(false);
      setRefundNotes('');
      onReturnUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as refunded');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLinkReplacement = async () => {
    if (!replacementOrderNumber.trim()) {
      toast.error('Please enter the replacement order number');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/${returnData.id}/link-replacement?replacement_order_number=${encodeURIComponent(replacementOrderNumber)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Replacement order linked successfully');
      setReplacementDialogOpen(false);
      setReplacementOrderNumber('');
      onReturnUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link replacement order');
    } finally {
      setLoading(false);
    }
  };
  
  // Available status transitions based on current status
  const getAvailableStatuses = () => {
    const transitions = {
      pending: ['accepted', 'rejected'],
      accepted: ['pickup_assigned'],
      pickup_assigned: ['picked_up', 'in_transit'],
      picked_up: ['in_transit', 'delivered'],
      in_transit: ['delivered', 'rto_in_transit'],
      delivered: ['qc_pending'],
      qc_pending: ['inwarded'],
      inwarded: ['refund_pending', 'completed'],
      refund_pending: ['completed'],
      rto_in_transit: ['delivered']
    };
    return transitions[returnData.status] || [];
  };
  
  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            
            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-lg bg-[#0f0f15] border-l border-white/10 z-50 overflow-y-auto"
              data-testid="return-detail-panel"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 bg-[#0f0f15]/95 backdrop-blur-xl border-b border-white/5 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <RotateCcw className="w-5 h-5 text-purple-400" />
                      {returnData.return_number}
                    </h2>
                    <p className="text-sm text-zinc-500">Order: {returnData.order_number}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="text-zinc-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                
                {/* Status & Type Badges */}
                <div className="flex items-center gap-2 mt-3">
                  <Badge className={`${status.bg} ${status.color} border ${status.border}`}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {status.label}
                  </Badge>
                  <Badge className={`${returnType.bg} ${returnType.color}`}>
                    <TypeIcon className="w-3 h-3 mr-1" />
                    {returnType.label}
                  </Badge>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 space-y-6">
                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2">
                  {returnData.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => setAcceptDialogOpen(true)}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                        data-testid="accept-return-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Accept
                      </Button>
                      <Button
                        onClick={() => setRejectDialogOpen(true)}
                        variant="destructive"
                        size="sm"
                        data-testid="reject-return-btn"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  
                  {returnData.status === 'accepted' && (
                    <Button
                      onClick={() => setPickupDialogOpen(true)}
                      className="bg-cyan-600 hover:bg-cyan-700"
                      size="sm"
                      data-testid="assign-pickup-btn"
                    >
                      <Truck className="w-4 h-4 mr-1" />
                      Assign Pickup
                    </Button>
                  )}
                  
                  {['inwarded', 'refund_pending'].includes(returnData.status) && (
                    <Button
                      onClick={() => setRefundDialogOpen(true)}
                      className="bg-amber-600 hover:bg-amber-700"
                      size="sm"
                      data-testid="mark-refunded-btn"
                    >
                      <DollarSign className="w-4 h-4 mr-1" />
                      Mark Refunded
                    </Button>
                  )}
                  
                  {(returnData.return_type === 'replace_same' || returnData.return_type === 'replace_different') && 
                   !returnData.replacement_order_id && (
                    <Button
                      onClick={() => setReplacementDialogOpen(true)}
                      variant="outline"
                      size="sm"
                      className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                      data-testid="link-replacement-btn"
                    >
                      <Link className="w-4 h-4 mr-1" />
                      Link Replacement
                    </Button>
                  )}
                  
                  {['rejected', 'cancelled'].includes(returnData.status) && (
                    <Button
                      onClick={handleReopen}
                      disabled={loading}
                      className="bg-purple-600 hover:bg-purple-700"
                      size="sm"
                      data-testid="reopen-return-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reopen Return
                    </Button>
                  )}
                  
                  {!['completed', 'cancelled'].includes(returnData.status) && (
                    <Button
                      onClick={openEditDialog}
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-zinc-300 hover:bg-white/5"
                      data-testid="edit-return-btn"
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit Return
                    </Button>
                  )}
                  
                  <Button
                    onClick={handlePrintSlip}
                    variant="outline"
                    size="sm"
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                    data-testid="print-pickup-slip-btn"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Pickup Slip
                  </Button>
                  
                  {getAvailableStatuses().length > 0 && (
                    <Button
                      onClick={() => setStatusDialogOpen(true)}
                      variant="outline"
                      size="sm"
                      className="border-white/10"
                      data-testid="update-status-btn"
                    >
                      <ChevronRight className="w-4 h-4 mr-1" />
                      Update Status
                    </Button>
                  )}
                </div>
                
                {/* Customer Information */}
                <div className="bg-white/[0.02] rounded-xl p-4 border border-white/5">
                  <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Customer Information
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-white">
                      <span className="font-medium">{returnData.customer_name}</span>
                    </div>
                    {returnData.customer_phone && (
                      <div className="flex items-center gap-2 text-zinc-400 text-sm">
                        <Phone className="w-4 h-4" />
                        {returnData.customer_phone}
                      </div>
                    )}
                    {returnData.customer_email && (
                      <div className="flex items-center gap-2 text-zinc-400 text-sm">
                        <Mail className="w-4 h-4" />
                        {returnData.customer_email}
                      </div>
                    )}
                    {returnData.pickup_address && (
                      <div className="flex items-start gap-2 text-zinc-400 text-sm mt-2">
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{typeof returnData.pickup_address === 'object' 
                          ? `${returnData.pickup_address.address1 || ''}, ${returnData.pickup_address.city || ''}, ${returnData.pickup_address.province || ''} ${returnData.pickup_address.zip || ''}`
                          : returnData.pickup_address
                        }</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Return Items */}
                <div className="bg-white/[0.02] rounded-xl p-4 border border-white/5">
                  <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Return Items ({returnData.items?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {returnData.items?.map((item, index) => (
                      <div key={index} className="bg-white/[0.02] rounded-lg p-3 border border-white/5">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-white">{item.product_name}</p>
                            {item.variant_title && (
                              <p className="text-xs text-zinc-500">{item.variant_title}</p>
                            )}
                            <p className="text-xs text-zinc-500 font-mono mt-1">SKU: {item.sku}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-white">Qty: {item.quantity_returning}</p>
                            <p className="text-xs text-zinc-500">of {item.quantity_ordered} ordered</p>
                          </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-white/5">
                          <p className="text-xs text-zinc-400">
                            <span className="text-zinc-500">Reason:</span> {item.reason}
                          </p>
                          {item.reason_notes && (
                            <p className="text-xs text-zinc-500 mt-1">{item.reason_notes}</p>
                          )}
                        </div>
                        {item.photos?.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {item.photos.map((photo, pIdx) => (
                              <a key={pIdx} href={photo} target="_blank" rel="noreferrer" className="w-12 h-12 rounded bg-white/5 overflow-hidden border border-white/10 hover:border-purple-400 transition-colors" data-testid={`return-item-photo-${pIdx}`}>
                                <img src={photo} alt="Return item" className="w-full h-full object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                        
                        {/* QC Info if available */}
                        {(item.good_qty !== undefined || item.damaged_qty !== undefined) && (
                          <div className="mt-2 pt-2 border-t border-white/5">
                            <p className="text-xs font-medium text-zinc-400 mb-1">QC Results:</p>
                            <div className="flex gap-3 text-xs">
                              <span className="text-green-400">Good: {item.good_qty || 0}</span>
                              <span className="text-red-400">Damaged: {item.damaged_qty || 0}</span>
                              <span className="text-amber-400">Missing: {item.missing_qty || 0}</span>
                            </div>
                            {item.rack_number && (
                              <p className="text-xs text-zinc-500 mt-1">Rack: {item.rack_number}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Financial Summary */}
                <div className="bg-white/[0.02] rounded-xl p-4 border border-white/5">
                  <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Financial Summary
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Subtotal</span>
                      <span className="text-white">{formatCurrency(returnData.subtotal || 0)}</span>
                    </div>
                    {returnData.shipping_refund > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Shipping Refund</span>
                        <span className="text-white">{formatCurrency(returnData.shipping_refund)}</span>
                      </div>
                    )}
                    {returnData.restocking_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Restocking Fee</span>
                        <span className="text-red-400">-{formatCurrency(returnData.restocking_fee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-white/10">
                      <span className="text-white font-medium">Refund Amount</span>
                      <span
                        className={`font-semibold ${(returnData.refund_amount || 0) < 0 ? 'text-red-400' : 'text-green-400'}`}
                        data-testid="return-refund-amount"
                      >
                        {formatCurrency(returnData.refund_amount || 0)}
                      </span>
                    </div>
                    {(returnData.refund_amount || 0) < 0 && (
                      <p className="text-[11px] text-red-400/70" data-testid="negative-refund-note">
                        Restocking fee exceeds the item value — no refund is due to the customer.
                      </p>
                    )}
                    {returnData.refund_method && (
                      <div className="flex justify-between text-xs pt-1">
                        <span className="text-zinc-500">Refund Method</span>
                        <span className={returnData.refund_method === 'store_credit' ? 'text-emerald-400' : 'text-cyan-400'} data-testid="return-refund-method">
                          {returnData.refund_method === 'store_credit' ? 'Store Credit' : 'Original Payment'}
                        </span>
                      </div>
                    )}
                    {returnData.store_credit_result && (
                      <div className="flex justify-between text-xs">
                        <span className="text-zinc-500">Shopify Store Credit</span>
                        <span className={returnData.store_credit_result.success ? 'text-emerald-400' : 'text-red-400'}>
                          {returnData.store_credit_result.success
                            ? `Issued · ${returnData.store_credit_result.amount} ${returnData.store_credit_result.currency}`
                            : `Failed · ${returnData.store_credit_result.error}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Pickup Information */}
                {(returnData.pickup_type || returnData.pickup_awb) && (
                  <div className="bg-white/[0.02] rounded-xl p-4 border border-white/5">
                    <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Pickup Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      {returnData.pickup_type && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Pickup Type</span>
                          <span className="text-white capitalize">{returnData.pickup_type.replace('_', ' ')}</span>
                        </div>
                      )}
                      {returnData.courier_type && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Courier</span>
                          <span className="text-white capitalize">{returnData.courier_type}</span>
                        </div>
                      )}
                      {returnData.assigned_driver_name && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Driver</span>
                          <span className="text-white">{returnData.assigned_driver_name}</span>
                        </div>
                      )}
                      {returnData.pickup_awb && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">AWB Number</span>
                          <span className="text-white font-mono">{returnData.pickup_awb}</span>
                        </div>
                      )}
                      {returnData.scheduled_pickup_date && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Scheduled Date</span>
                          <span className="text-white">{formatDate(returnData.scheduled_pickup_date)}</span>
                        </div>
                      )}
                      {(returnData.pickup_proof_photos || []).length > 0 && (
                        <div className="pt-2">
                          <span className="text-zinc-400 block mb-1.5">Pickup Proof (driver)</span>
                          <div className="flex gap-2 flex-wrap" data-testid="pickup-proof-photos">
                            {returnData.pickup_proof_photos.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                <img src={url} alt="pickup proof" className="w-16 h-16 object-cover rounded-lg border border-white/10 hover:opacity-80" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Replacement Order */}
                {returnData.replacement_order_number && (
                  <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20">
                    <h3 className="text-sm font-medium text-purple-300 mb-2 flex items-center gap-2">
                      <ArrowLeftRight className="w-4 h-4" />
                      Replacement Order
                    </h3>
                    <p className="text-white font-mono">{returnData.replacement_order_number}</p>
                  </div>
                )}
                
                {/* Timeline */}
                <div className="bg-white/[0.02] rounded-xl p-4 border border-white/5">
                  <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Timeline
                  </h3>
                  <div className="space-y-3">
                    {returnData.timeline?.map((event, index) => (
                      <div key={index} className="flex gap-3">
                        <div className="w-2 h-2 rounded-full bg-purple-400 mt-1.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-white">{event.message}</p>
                          <p className="text-xs text-zinc-500">{formatDate(event.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Customer Notes */}
                {returnData.customer_notes && (
                  <div className="bg-white/[0.02] rounded-xl p-4 border border-white/5">
                    <h3 className="text-sm font-medium text-zinc-400 mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Customer Notes
                    </h3>
                    <p className="text-sm text-zinc-300">{returnData.customer_notes}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* Accept Dialog */}
      {/* Edit Return Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#16161d] border-white/10 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="edit-return-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Return</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-3">
              <Label className="text-zinc-400">Items & Reasons</Label>
              {editItems.map((it, idx) => (
                <div key={it.sku} className="bg-white/[0.03] rounded-lg p-3 border border-white/5 space-y-2">
                  <p className="text-sm text-white font-medium truncate">{it.product_name} <span className="text-zinc-500">({it.sku})</span></p>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        value={it.reason}
                        onChange={(e) => setEditItems(prev => prev.map((p, i) => i === idx ? { ...p, reason: e.target.value } : p))}
                        placeholder="Return reason"
                        className="bg-white/5 border-white/10 text-white"
                        data-testid={`edit-reason-${it.sku}`}
                      />
                    </div>
                    <Input
                      type="number"
                      min="1"
                      max={it.quantity_ordered || undefined}
                      value={it.quantity_returning}
                      onChange={(e) => setEditItems(prev => prev.map((p, i) => i === idx ? { ...p, quantity_returning: e.target.value } : p))}
                      className="w-20 bg-white/5 border-white/10 text-white"
                      data-testid={`edit-qty-${it.sku}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div>
              <Label className="text-zinc-400">Restocking Fee</Label>
              <Input
                type="number"
                min="0"
                value={editFee}
                onChange={(e) => setEditFee(e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1"
                data-testid="edit-restocking-fee"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-zinc-400">Extra Charges (deducted from refund)</Label>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 border-white/10 text-zinc-300"
                  onClick={() => setEditCharges(prev => [...prev, { label: '', amount: 0 }])}
                  data-testid="add-charge-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {editCharges.map((c, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      value={c.label}
                      onChange={(e) => setEditCharges(prev => prev.map((p, i) => i === idx ? { ...p, label: e.target.value } : p))}
                      placeholder="Charge name (e.g. Pickup fee)"
                      className="flex-1 bg-white/5 border-white/10 text-white"
                      data-testid={`charge-label-${idx}`}
                    />
                    <Input
                      type="number"
                      value={c.amount}
                      onChange={(e) => setEditCharges(prev => prev.map((p, i) => i === idx ? { ...p, amount: e.target.value } : p))}
                      className="w-28 bg-white/5 border-white/10 text-white"
                      data-testid={`charge-amount-${idx}`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-red-400 hover:bg-red-500/10 h-8 w-8"
                      onClick={() => setEditCharges(prev => prev.filter((_, i) => i !== idx))}
                      data-testid={`remove-charge-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {editCharges.length === 0 && <p className="text-xs text-zinc-600">No extra charges</p>}
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Customer Notes</Label>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1"
                rows={2}
                data-testid="edit-customer-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-return-btn">
              {loading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={acceptDialogOpen} onOpenChange={setAcceptDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Accept Return
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Restocking Fee (optional)</Label>
              <Input
                type="number"
                value={restockingFee}
                onChange={(e) => setRestockingFee(parseFloat(e.target.value) || 0)}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="0.00"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Refund will be: {formatCurrency((returnData?.subtotal || 0) - restockingFee)}
              </p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={acceptNotes}
                onChange={(e) => setAcceptNotes(e.target.value)}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Add any notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleAccept} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Accept Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              Reject Return
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for Rejection *</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Explain why this return is being rejected..."
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleReject} disabled={loading || !rejectReason.trim()} variant="destructive">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reject Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Assign Pickup Dialog */}
      <Dialog open={pickupDialogOpen} onOpenChange={setPickupDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-cyan-400" />
              Assign Pickup
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Pickup Type</Label>
              <RadioGroup value={pickupType} onValueChange={setPickupType} className="mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="courier" id="courier" />
                  <Label htmlFor="courier" className="cursor-pointer">Courier</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="driver" id="driver" />
                  <Label htmlFor="driver" className="cursor-pointer">Driver</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="self_ship" id="self_ship" />
                  <Label htmlFor="self_ship" className="cursor-pointer">Self Ship (Customer)</Label>
                </div>
              </RadioGroup>
            </div>
            
            {pickupType === 'courier' && (
              <div>
                <Label>Select Courier</Label>
                <Select value={courierType} onValueChange={setCourierType}>
                  <SelectTrigger className="bg-white/5 border-white/10 mt-1">
                    <SelectValue placeholder="Select courier..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {courierIntegrations.map((c) => (
                      <SelectItem key={c.id} value={c.courier_type}>{c.courier_type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {pickupType === 'driver' && (
              <div>
                <Label>Select Driver</Label>
                <Select value={driverId} onValueChange={setDriverId}>
                  <SelectTrigger className="bg-white/5 border-white/10 mt-1">
                    <SelectValue placeholder="Select driver..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Scheduled Date (optional)</Label>
              <DateInput
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="bg-white/5 border-white/10 mt-1"
              />
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={pickupNotes}
                onChange={(e) => setPickupNotes(e.target.value)}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Special pickup instructions..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickupDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleAssignPickup} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign Pickup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Update Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChevronRight className="w-5 h-5 text-purple-400" />
              Update Status
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-white/5 border-white/10 mt-1">
                  <SelectValue placeholder="Select new status..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {getAvailableStatuses().map((s) => (
                    <SelectItem key={s} value={s}>
                      {statusConfig[s]?.label || s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Add any notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleUpdateStatus} disabled={loading || !newStatus} className="bg-purple-600 hover:bg-purple-700">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Mark Refunded Dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-400" />
              Mark as Refunded
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-sm text-amber-300">
                Refund Amount: <span className="font-semibold">{formatCurrency(returnData?.refund_amount || 0)}</span>
              </p>
            </div>
            <div>
              <Label>Refund Notes (optional)</Label>
              <Textarea
                value={refundNotes}
                onChange={(e) => setRefundNotes(e.target.value)}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Transaction ID, payment method, etc..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleMarkRefunded} disabled={loading} className="bg-amber-600 hover:bg-amber-700">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Link Replacement Order Dialog */}
      <Dialog open={replacementDialogOpen} onOpenChange={setReplacementDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-purple-400" />
              Link Replacement Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Replacement Order Number</Label>
              <Input
                value={replacementOrderNumber}
                onChange={(e) => setReplacementOrderNumber(e.target.value)}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="e.g. #1234 or ORD-5678"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplacementDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleLinkReplacement} disabled={loading || !replacementOrderNumber.trim()} className="bg-purple-600 hover:bg-purple-700">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Link Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReturnDetailPanel;
